import { setupColourScheme } from "./ColourMode.ts";
import { LOAD_CONFIG_KEY, loadConfig } from "./Loaders.ts";
import Logo from "./Logo.tsx";
import { useWebsocket } from "./Websocket.ts";
import { useUIStore } from "../components/Store.ts";
import ForgotPassword from "../panels/ForgotPassword.tsx";
import { DefaultHomeChild, Home, HomeSearch } from "../panels/Home.tsx";
import Login from "../panels/Login.tsx";
import PasswordReset from "../panels/PasswordReset.tsx";
import Register from "../panels/Register.tsx";
import { Duplicates } from "../tabs/Duplicates.tsx";
import DuplicatesOneTag from "../tabs/DuplicatesOneTag.tsx";
import { History, RecentEntries, TopEntries } from "../tabs/Entries.tsx";
import Feed from "../tabs/Feed.tsx";
import Feeds from "../tabs/Feeds.tsx";
import { Search } from "../tabs/Search.tsx";
import Tag from "../tabs/Tag.tsx";
import Tags from "../tabs/Tags.tsx";
import Users from "../tabs/Users.tsx";
import { useQuery } from "@tanstack/react-query";
import {
  createHashHistory,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  RouterProvider,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect } from "react";

function errorComponent({ error }: { error: Error }) {
  return (
    <div id="error-page" data-testid="error-page">
      <h1>
        <Logo />
        Oops!
      </h1>
      <p>Sorry, an unexpected error has occurred.</p>
      <p>
        <i>{error.message}</i>
      </p>
    </div>
  );
}

function App() {
  const store = useUIStore.getState();
  const sessionId = store.sessionId;
  const config = useQuery({
    queryKey: LOAD_CONFIG_KEY,
    queryFn: loadConfig(sessionId),
  });
  useWebsocket();
  useEffect(() => {
    setupColourScheme();
  }, []);
  const rootRoute = createRootRoute({
    errorComponent,
    notFoundComponent: () =>
      errorComponent({ error: new Error("Page not found!") }),
  });
  const indexRoute = createRoute({
    id: "homePathless",
    getParentRoute: () => rootRoute,
    component: Home,
    validateSearch: (search: Record<string, unknown>): HomeSearch => ({
      token: search?.token as string | undefined,
    }),
    loader: ({ location }) => {
      if (config.isError) {
        throw new Response("Config Error", { status: 500 });
      }
      const store = useUIStore.getState();
      const accountId = store.loginData?.id;
      const loggedIn = accountId !== undefined;
      if (!loggedIn) {
        console.debug(
          "redirect for login",
          loggedIn,
          location.pathname,
          location.search,
        );
        throw redirect({
          href: `/login?redirect=${encodeURIComponent(
            location.pathname + location.searchStr,
          )}`,
        });
      }
      return null;
    },
  });
  const routeTree = rootRoute.addChildren([
    indexRoute.addChildren([
      createRoute({
        getParentRoute: () => indexRoute,
        path: "/",
        component: () => <DefaultHomeChild />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "best",
        component: () => <TopEntries sessionId={sessionId} />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "recent",
        component: () => <RecentEntries sessionId={sessionId} />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "Feeds",
        component: () => <Feeds />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "feed/$feedId",
        component: () => <Feed />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "users",
        component: () => <Users />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "duplicates",
        component: () => <Duplicates />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "duplicates/$tagId",
        component: () => <DuplicatesOneTag />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "history",
        component: () => <History sessionId={sessionId} />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "tags",
        component: () => <Tags />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "tag/$tagId",
        component: () => <Tag />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        path: "search",
        component: () => <Search sessionId={sessionId} />,
      }),
      createRoute({
        getParentRoute: () => indexRoute,
        // Dummy entry, gets dealt with by Home.tsx
        path: "Registration",
      }),
    ]),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "Login",
      component: () => <Login />,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "Register",
      component: () => <Register />,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "ForgotPassword",
      component: () => <ForgotPassword />,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "PasswordReset",
      component: () => <PasswordReset />,
    }),
  ]);
  const router = createRouter({
    routeTree,
    history: createHashHistory(),
    search: { strict: false },
  });
  return (
    <>
      <RouterProvider router={router} />
      <TanStackRouterDevtools router={router} />
    </>
  );
}

export default App;
